import React, { useState, useEffect } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { CONTACT_ENDPOINT } from "@/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BOOKING_EVENT = "open-booking-modal";
export const openBookingModal = () =>
  window.dispatchEvent(new CustomEvent(BOOKING_EVENT));

export default function BookingModal() {
  const { t } = useI18n();
  const m = t.bookingModal;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const onOpen = () => {
      setSent(false);
      setOpen(true);
    };
    window.addEventListener(BOOKING_EVENT, onOpen);
    return () => window.removeEventListener(BOOKING_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      window.__lenis?.start();
    };
  }, [open]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error(m.errFill);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(CONTACT_ENDPOINT, form);
      setSent(true);
      setForm({ name: "", email: "", phone: "", message: "" });
      toast.success(m.success);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : m.errSend
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="booking-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div
            className="absolute inset-0 bg-ink/70 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            data-testid="booking-modal-backdrop"
          />

          <motion.div
            data-lenis-prevent
            role="dialog"
            aria-modal="true"
            aria-label={m.ariaLabel}
            className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto bg-canvas border border-ink/10 shadow-2xl"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="booking-modal-close"
              aria-label={m.close}
              className="absolute top-5 right-5 text-ink/50 hover:text-brand-red transition-colors z-10"
            >
              <X size={22} strokeWidth={1.4} />
            </button>

            <div className="p-8 md:p-10">
              <p className="overline mb-3">{m.overline}</p>
              <h2 className="serif text-3xl md:text-4xl font-light leading-tight tracking-tight text-ink">
                {m.headingA}{" "}
                <span className="italic text-brand-red/80">{m.headingB}</span>
              </h2>
              <p className="mt-4 text-ink-muted text-sm md:text-base leading-relaxed">
                {m.intro}
              </p>

              {sent ? (
                <div className="mt-8 flex flex-col items-start gap-6">
                  <div className="flex items-center gap-3 text-brand-red border-l-2 border-brand-red/60 pl-4 py-2">
                    <CheckCircle2 size={20} strokeWidth={1.2} />
                    <p className="serif italic text-lg">
                      {m.sent}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    data-testid="booking-modal-done"
                    className="btn-accent"
                  >
                    {m.close}
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={onSubmit}
                  data-testid="booking-form"
                  className="mt-8 space-y-6"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="overline block mb-2" htmlFor="booking-name">
                        {m.formName} <span className="text-brand-red">*</span>
                      </label>
                      <input
                        id="booking-name"
                        name="name"
                        value={form.name}
                        onChange={onChange}
                        required
                        data-testid="booking-input-name"
                        className="field-underline"
                        placeholder={m.phName}
                      />
                    </div>
                    <div>
                      <label className="overline block mb-2" htmlFor="booking-email">
                        {m.formEmail} <span className="text-brand-red">*</span>
                      </label>
                      <input
                        id="booking-email"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={onChange}
                        required
                        data-testid="booking-input-email"
                        className="field-underline"
                        placeholder={m.phEmail}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="overline block mb-2" htmlFor="booking-phone">
                      {m.formPhone} <span className="text-brand-red">*</span>
                    </label>
                    <input
                      id="booking-phone"
                      name="phone"
                      value={form.phone}
                      onChange={onChange}
                      required
                      data-testid="booking-input-phone"
                      className="field-underline"
                      placeholder={m.phPhone}
                    />
                  </div>

                  <div>
                    <label className="overline block mb-2" htmlFor="booking-message">
                      {m.formMessage} <span className="text-brand-red">*</span>
                    </label>
                    <textarea
                      id="booking-message"
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      required
                      rows={4}
                      data-testid="booking-input-message"
                      className="field-underline resize-none"
                      placeholder={m.phMessage}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="booking-form-submit"
                    className="btn-accent w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? m.submitting : m.submit}
                  </button>

                  <p
                    data-testid="booking-form-consent"
                    className="text-ink-muted text-xs leading-relaxed text-center"
                  >
                    {m.consent}
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
